// ============================================================================
// LOGIN AFILIADO
// ============================================================================
// Arquivo: src/pages/LoginAfiliado.tsx
// UPDATED: 2026-03-26
// ✅ Conectado à API real via authService
// ✅ Campo alterado de ID para e-mail (backend autentica por email)
// ✅ affiliateId, schoolName, logoUrl populados no Redux após login
// ============================================================================

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, LogIn, AlertCircle } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { login } from '../store/slices/authSlice';
import { authService } from '../services/authService';

const LoginAfiliado: React.FC = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const [formData, setFormData]       = useState({ login: '', senha: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // ✅ Chama a API real
      const response = await authService.login({
        email:    formData.login.trim(),  // aceita ID ou email
        password: formData.senha,
        userType: 'affiliate',
      });

      // ✅ getCachedUser() já tem affiliateId, schoolName, logoUrl
      //    (authService.saveSession → normalizeUser mapeou tudo)
      const user = authService.getCachedUser();

      if (!user) throw new Error('Erro ao carregar dados do usuário');

      // ✅ Despacha com todos os campos para o Redux
      dispatch(login({
        user: {
          id:          user.id,
          name:        user.name,
          email:       user.email,
          role:        'affiliate',
          userType:    'affiliate',
          systemId:    user.systemId,
          affiliateId: user.affiliateId,   // ← chave para cadastro de alunos
          schoolName:  user.schoolName,    // ← aparece no portal
          logoUrl:     user.logoUrl,       // ← logo para diplomas
          status:      user.status,
        },
        token: response.accessToken,
      }));

      navigate('/portal/filiado');
    } catch (err: any) {
      // class-validator retorna message como array — normaliza para string
      const raw = err?.response?.data?.message ?? err?.message ?? '';
      const msg = Array.isArray(raw) ? raw.join(', ') : String(raw);

      if (msg.toLowerCase().includes('credential') || msg.toLowerCase().includes('invalid')) {
        setError('ID/e-mail ou senha incorretos. Tente novamente.');
      } else if (msg.toLowerCase().includes('pending') || msg.toLowerCase().includes('pendente')) {
        setError('Seu cadastro ainda está pendente de aprovação pela Federação.');
      } else if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('unauthorized')) {
        setError('ID/e-mail ou senha incorretos. Tente novamente.');
      } else if (msg) {
        setError(msg);
      } else {
        setError('Erro ao conectar. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

        .afi-root * { box-sizing: border-box; margin: 0; padding: 0; }

        .afi-root {
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh;
          background: #0c0a06;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          padding: 24px;
        }

        .afi-bg { position: absolute; inset: 0; z-index: 0; }
        .afi-bg::before {
          content: '';
          position: absolute;
          width: 600px; height: 600px;
          top: -200px; left: -200px;
          background: radial-gradient(circle, rgba(180,130,30,0.16) 0%, transparent 70%);
          animation: afi-drift1 14s ease-in-out infinite alternate;
        }
        .afi-bg::after {
          content: '';
          position: absolute;
          width: 500px; height: 500px;
          bottom: -150px; right: -150px;
          background: radial-gradient(circle, rgba(140,90,10,0.13) 0%, transparent 70%);
          animation: afi-drift2 17s ease-in-out infinite alternate;
        }
        @keyframes afi-drift1 { from { transform: translate(0,0); } to { transform: translate(70px, 90px); } }
        @keyframes afi-drift2 { from { transform: translate(0,0); } to { transform: translate(-60px, -70px); } }

        .afi-grid {
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(200,160,60,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(200,160,60,0.04) 1px, transparent 1px);
          background-size: 48px 48px;
        }

        .afi-card {
          position: relative; z-index: 1;
          width: 100%; max-width: 460px;
          animation: afi-fadeUp 0.6s cubic-bezier(0.16,1,0.3,1) both;
        }
        @keyframes afi-fadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .afi-back-link {
          display: inline-flex; align-items: center; gap: 8px;
          color: rgba(255,255,255,0.5);
          font-size: 13px; font-weight: 500;
          text-decoration: none;
          margin-bottom: 24px;
          transition: color 0.2s;
        }
        .afi-back-link:hover { color: #d4a435; }

        .afi-logo-wrap {
          text-align: center; margin-bottom: 28px;
          animation: afi-fadeUp 0.6s 0.05s cubic-bezier(0.16,1,0.3,1) both;
        }
        .afi-logo-ring {
          display: inline-flex; align-items: center; justify-content: center;
          width: 96px; height: 96px; border-radius: 50%;
          background: linear-gradient(135deg, #2a1e08 0%, #160f02 100%);
          border: 2px solid rgba(200,160,60,0.35);
          box-shadow: 0 0 0 8px rgba(180,130,30,0.08), 0 20px 60px rgba(0,0,0,0.5);
          margin: 0 auto 18px; overflow: hidden;
          transition: box-shadow 0.3s;
        }
        .afi-logo-ring:hover {
          box-shadow: 0 0 0 12px rgba(180,130,30,0.14), 0 24px 70px rgba(0,0,0,0.6);
        }
        .afi-logo-ring img {
          width: 64px; height: 64px; object-fit: contain;
          filter: drop-shadow(0 2px 8px rgba(210,170,60,0.4));
        }
        .afi-title {
          font-family: 'Playfair Display', serif;
          font-size: 28px; font-weight: 900;
          color: #fff; letter-spacing: -0.5px; line-height: 1.1;
        }
        .afi-subtitle { color: #7a6a40; font-size: 13px; font-weight: 400; margin-top: 6px; }

        .afi-panel {
          background: rgba(255,255,255,0.035);
          border: 1px solid rgba(200,160,60,0.12);
          border-radius: 20px; padding: 36px;
          backdrop-filter: blur(20px);
          box-shadow: 0 32px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(200,160,60,0.08);
          animation: afi-fadeUp 0.6s 0.1s cubic-bezier(0.16,1,0.3,1) both;
        }

        .afi-panel-header {
          display: flex; align-items: center; gap: 10px;
          margin-bottom: 28px; padding-bottom: 20px;
          border-bottom: 1px solid rgba(200,160,60,0.1);
        }
        .afi-badge {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(180,130,30,0.15);
          border: 1px solid rgba(200,160,60,0.28);
          border-radius: 30px; padding: 5px 12px;
          font-size: 11px; font-weight: 600;
          color: #d4a435; letter-spacing: 0.8px; text-transform: uppercase;
        }
        .afi-badge-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #d4a435;
          box-shadow: 0 0 6px rgba(212,164,53,0.8);
          animation: afi-pulse 2s ease-in-out infinite;
        }
        @keyframes afi-pulse {
          0%,100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.6; transform: scale(0.85); }
        }
        .afi-panel-title {
          font-family: 'Playfair Display', serif;
          font-size: 20px; font-weight: 700;
          color: #e8dfc0; margin-left: auto;
        }

        .afi-error {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 12px 16px;
          background: rgba(220,40,40,0.1); border: 1px solid rgba(220,40,40,0.25);
          border-radius: 12px; margin-bottom: 22px;
          animation: afi-shake 0.35s ease;
        }
        @keyframes afi-shake {
          0%,100% { transform: translateX(0); }
          20%      { transform: translateX(-6px); }
          40%      { transform: translateX(6px); }
          60%      { transform: translateX(-4px); }
          80%      { transform: translateX(4px); }
        }
        .afi-error svg { color: #f87171; flex-shrink: 0; margin-top: 1px; }
        .afi-error p   { color: #fca5a5; font-size: 13px; font-weight: 500; line-height: 1.4; }

        .afi-field { margin-bottom: 18px; }
        .afi-label {
          display: block; font-size: 11.5px; font-weight: 600;
          letter-spacing: 0.7px; text-transform: uppercase;
          color: #7a6a40; margin-bottom: 8px;
        }
        .afi-hint { color: #4a3e22; font-size: 11px; margin-top: 6px; }
        .afi-input-wrap { position: relative; }
        .afi-input {
          width: 100%; padding: 13px 16px;
          background: rgba(255,255,255,0.04);
          border: 1.5px solid rgba(200,160,60,0.15);
          border-radius: 12px; color: #e8dfc0;
          font-family: 'DM Sans', sans-serif; font-size: 14.5px; font-weight: 500;
          outline: none; transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
          caret-color: #d4a435;
        }
        .afi-input::placeholder { color: #3d3218; }
        .afi-input:focus {
          border-color: rgba(212,164,53,0.6);
          background: rgba(140,100,20,0.08);
          box-shadow: 0 0 0 3px rgba(212,164,53,0.1);
        }
        .afi-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .afi-input.with-btn { padding-right: 46px; }

        .afi-eye {
          position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: #4a3e22; display: flex; align-items: center; justify-content: center;
          padding: 0; transition: color 0.2s;
        }
        .afi-eye:hover { color: #7a6a40; }

        .afi-row {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 22px; margin-top: -4px;
        }
        .afi-check-label {
          display: flex; align-items: center; gap: 8px; cursor: pointer;
          color: rgba(255,255,255,0.45); font-size: 13px;
        }
        .afi-check-label input[type="checkbox"] {
          width: 15px; height: 15px; accent-color: #d4a435; border-radius: 4px;
        }
        .afi-forgot {
          color: #d4a435; font-size: 13px; font-weight: 600;
          text-decoration: none; transition: color 0.2s;
        }
        .afi-forgot:hover { color: #f0c060; }

        .afi-btn {
          width: 100%; padding: 14px; border: none; border-radius: 12px;
          background: linear-gradient(135deg, #b8860b 0%, #8b6508 100%);
          color: #fff; font-family: 'DM Sans', sans-serif;
          font-size: 15px; font-weight: 700; letter-spacing: 0.3px;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          gap: 8px;
          box-shadow: 0 8px 32px rgba(180,130,20,0.35);
          transition: transform 0.18s, box-shadow 0.18s, opacity 0.18s;
          position: relative; overflow: hidden;
        }
        .afi-btn::before {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%);
          border-radius: inherit;
        }
        .afi-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 40px rgba(180,130,20,0.45);
        }
        .afi-btn:active:not(:disabled) { transform: translateY(0); }
        .afi-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        .afi-divider {
          display: flex; align-items: center; gap: 12px; margin: 26px 0;
        }
        .afi-divider-line { flex: 1; border: none; border-top: 1px solid rgba(200,160,60,0.1); }
        .afi-divider-text { color: #4a3e22; font-size: 12px; white-space: nowrap; }

        .afi-cta { text-align: center; }
        .afi-cta p { color: rgba(255,255,255,0.4); font-size: 13px; margin-bottom: 14px; }
        .afi-cta-btn {
          display: inline-block;
          background: rgba(212,164,53,0.12);
          border: 1.5px solid rgba(212,164,53,0.35);
          color: #d4a435; font-weight: 700; font-size: 14px;
          padding: 11px 28px; border-radius: 10px;
          text-decoration: none; transition: all 0.2s;
        }
        .afi-cta-btn:hover {
          background: rgba(212,164,53,0.22);
          border-color: rgba(212,164,53,0.6);
          transform: translateY(-1px);
        }

        .afi-stats {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 12px; margin-top: 20px;
          animation: afi-fadeUp 0.6s 0.2s cubic-bezier(0.16,1,0.3,1) both;
        }
        .afi-stat {
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(200,160,60,0.1);
          border-radius: 14px; padding: 16px;
          text-align: center; backdrop-filter: blur(10px);
        }
        .afi-stat-num {
          font-family: 'Playfair Display', serif;
          font-size: 24px; font-weight: 900; color: #d4a435;
        }
        .afi-stat-label { color: rgba(255,255,255,0.35); font-size: 11px; margin-top: 4px; }

        .afi-footer {
          text-align: center; margin-top: 20px;
          animation: afi-fadeUp 0.6s 0.25s cubic-bezier(0.16,1,0.3,1) both;
        }
        .afi-footer p { color: #3a3020; font-size: 12px; margin-bottom: 6px; }
        .afi-footer a { color: #4ade80; font-size: 13px; font-weight: 600; text-decoration: none; transition: color 0.2s; }
        .afi-footer a:hover { color: #86efac; }

        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div className="afi-root">
        <div className="afi-bg"><div className="afi-grid" /></div>

        <div className="afi-card">

          <Link to="/" className="afi-back-link">
            <ArrowLeft size={16} /> Voltar para Home
          </Link>

          <div className="afi-logo-wrap">
            <div className="afi-logo-ring">
              <img src="/imagem/Bras.png" alt="Federação dos Profissionais da Beleza" />
            </div>
            <h1 className="afi-title">Portal do Filiado</h1>
            <p className="afi-subtitle">Gerencie sua escola e alunos</p>
          </div>

          <div className="afi-panel">
            <div className="afi-panel-header">
              <span className="afi-badge">
                <span className="afi-badge-dot" /> Área Restrita
              </span>
              <span className="afi-panel-title">Escolas & Profissionais</span>
            </div>

            {error && (
              <div className="afi-error">
                <AlertCircle size={16} />
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>

              {/* ID ou Email */}
              <div className="afi-field">
                <label className="afi-label">ID do Filiado ou E-mail</label>
                <div className="afi-input-wrap">
                  <input
                    type="text"
                    value={formData.login}
                    onChange={e => { setFormData({ ...formData, login: e.target.value }); setError(''); }}
                    placeholder="FIL-2026-XXXXXX ou email@escola.com"
                    disabled={loading}
                    className="afi-input"
                    required
                  />
                </div>
                <p className="afi-hint">Use seu ID (ex: FIL-2026-XXXX) ou e-mail cadastrado</p>
              </div>

              {/* Senha */}
              <div className="afi-field">
                <label className="afi-label">Senha</label>
                <div className="afi-input-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.senha}
                    onChange={e => { setFormData({ ...formData, senha: e.target.value }); setError(''); }}
                    placeholder="••••••••"
                    disabled={loading}
                    className="afi-input with-btn"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(s => !s)} className="afi-eye" tabIndex={-1}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="afi-hint">Senha padrão enviada pelo administrador: fil@feder26</p>
              </div>

              {/* Remember / Forgot */}
              <div className="afi-row">
                <label className="afi-check-label">
                  <input type="checkbox" /> Lembrar-me
                </label>
                <Link to="/recuperar-senha" className="afi-forgot">Esqueci minha senha</Link>
              </div>

              <button
                type="submit"
                disabled={loading || !formData.login || !formData.senha}
                className="afi-btn"
              >
                {loading
                  ? <><div style={{ width:18, height:18, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 1s linear infinite' }} /> Entrando...</>
                  : <><LogIn size={18} /> Acessar Portal</>
                }
              </button>
            </form>

            <div className="afi-divider">
              <hr className="afi-divider-line" />
              <span className="afi-divider-text">Ainda não é Filiado?</span>
              <hr className="afi-divider-line" />
            </div>

            <div className="afi-cta">
              <p>Torne sua escola parte da rede oficial</p>
              <Link to="/filiacao" className="afi-cta-btn">Cadastre sua Escola</Link>
            </div>
          </div>

          <div className="afi-stats">
            <div className="afi-stat">
              <div className="afi-stat-num">1000+</div>
              <div className="afi-stat-label">Escolas Filiadas</div>
            </div>
            <div className="afi-stat">
              <div className="afi-stat-num">50mil+</div>
              <div className="afi-stat-label">Alunos Certificados</div>
            </div>
          </div>

          <div className="afi-footer">
            <p>Problemas para acessar? Entre em contato</p>
            <a href="https://wa.me/5598970190739" target="_blank" rel="noopener noreferrer">
              WhatsApp: (98) 97019-0739
            </a>
          </div>

        </div>
      </div>
    </>
  );
};

export default LoginAfiliado;
